import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, DropdownToggle, DropdownMenu } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useTenant } from "../../../hooks/useTenant";
import { useDebounce } from "../../../hooks/useDebounce";
import MemberService from "../../../services/member_service";

const GlobalMemberSearch = () => {
    const navigate = useNavigate();
    const { idTenant } = useTenant();

    const [allMembers, setAllMembers] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [isOpen, setIsOpen] = useState(false);

    const debouncedSearch = useDebounce(searchText, 400);
    const inputRef = useRef(null);

    // Carrega todos os membros no mount
    useEffect(() => {
        if (!idTenant) return;
        const loadMembers = async () => {
            try {
                const result = await MemberService.getMembers(idTenant);
                setAllMembers(result || []);
            } catch (e) {
                console.error("Erro ao carregar membros no search global:", e);
            }
        };
        loadMembers();
    }, [idTenant]);

    // Filtro
    const candidates = useMemo(() => {
        const q = (debouncedSearch || "").trim().toLowerCase();
        if (!q || q.length < 2) return [];

        return (allMembers || [])
            .filter(m => {
                const name = (m.fullName || m.name || "").toLowerCase();
                return name.includes(q);
            })
            .map(m => ({
                id: m.id,
                name: m.fullName || m.name || "Membro",
                photo: m.photoUrl || null,
                idMember: m.memberNumber || "",
            }))
            .slice(0, 8); // exibe até 8 no popup
    }, [allMembers, debouncedSearch]);

    const showNoResults = useMemo(() => {
        const q = (debouncedSearch || "").trim();
        if (!q || q.length < 2) return false;
        return candidates.length === 0;
    }, [debouncedSearch, candidates.length]);

    // Controla abertura do dropdown
    useEffect(() => {
        if (candidates.length > 0 || showNoResults) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    }, [candidates, showNoResults]);

    const handleSelect = (member) => {
        setSearchText("");
        setIsOpen(false);
        navigate(`/members/${member.id}`);
    };

    const handleToggle = () => {
        // Se já tiver resultados ou texto de no-results, pode clicar p/ abrir/fechar.
        if (candidates.length > 0 || showNoResults) {
            setIsOpen(!isOpen);
        }
    };

    return (
        <Dropdown
            isOpen={isOpen}
            toggle={handleToggle}
            className="d-none d-lg-inline-block me-3"
            style={{ width: '360px' }}
        >
            <DropdownToggle tag="div" className="w-100" caret={false}>
                <div className="position-relative w-100">
                    <i
                        className="mdi mdi-account-search position-absolute"
                        style={{
                            left: "12px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            fontSize: "18px",
                            color: "rgba(255, 255, 255, 0.7)",
                            pointerEvents: "none"
                        }}
                    />
                    <input
                        ref={inputRef}
                        type="text"
                        className="form-control text-white"
                        placeholder="Buscar aluno..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onFocus={() => {
                            if (candidates.length > 0 || showNoResults) setIsOpen(true);
                        }}
                        style={{
                            paddingLeft: "38px",
                            paddingRight: searchText ? "38px" : "12px",
                            fontSize: "14px",
                            backgroundColor: "#898b92ff", // cor cinza como solicitado
                            border: "none",
                            borderRadius: "6px",
                            height: "44px",
                            width: "100%",
                            boxShadow: "none"
                        }}
                    />
                    {searchText && (
                        <i
                            className="mdi mdi-close-circle position-absolute cursor-pointer"
                            style={{
                                right: "12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "18px",
                                color: "rgba(255, 255, 255, 0.7)",
                                cursor: "pointer"
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSearchText("");
                                setIsOpen(false);
                                inputRef.current?.focus();
                            }}
                        />
                    )}
                </div>
            </DropdownToggle>

            <DropdownMenu
                className="shadow-lg p-0"
                container="body" // <--- ISTO GARANTE QUE O MENU PASSARÁ POR CIMA DE TUDO!
                style={{
                    width: "360px",
                    backgroundColor: "#020308",
                    border: "1px solid rgba(255, 255, 255, 0.12)",
                    borderRadius: "8px",
                    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.5)",
                    marginTop: "6px",
                    overflow: "hidden",
                    zIndex: 1050
                }}
            >
                {/* Header invisível se quisermos por algo, mas listaremos direto */}
                <div className="px-3 py-2 d-flex align-items-center justify-content-between" style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <h6 className="m-0 fw-bold text-white font-size-13 d-flex align-items-center gap-2">
                        <i className="mdi mdi-account-search" style={{ color: "#00d1ff" }}></i>
                        Resultados da busca
                    </h6>
                </div>

                {showNoResults ? (
                    <div className="px-4 py-4 text-center">
                        <i className="mdi mdi-account-search-outline d-block mb-2" style={{ fontSize: "36px", color: "rgba(255,255,255,0.15)" }}></i>
                        <p className="mb-0 font-size-13" style={{ color: "rgba(255,255,255,0.4)" }}>Nenhum aluno encontrado.</p>
                    </div>
                ) : (
                    <SimpleBar style={{ maxHeight: 320 }}>
                        {candidates.map((c) => (
                            <div
                                key={c.id}
                                className="px-3 py-2 position-relative d-flex align-items-center gap-2"
                                style={{
                                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                                    transition: "background-color 0.3s",
                                    cursor: "pointer"
                                }}
                                onClick={() => handleSelect(c)}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)"}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                            >
                                <div
                                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                    style={{
                                        width: 36,
                                        height: 36,
                                        backgroundColor: "rgba(0, 209, 255, 0.1)",
                                    }}
                                >
                                    {c.photo ? (
                                        <img src={c.photo} alt="" className="rounded-circle" style={{ width: 36, height: 36, objectFit: "cover" }} />
                                    ) : (
                                        <i className="mdi mdi-account font-size-18" style={{ color: "#00d1ff" }}></i>
                                    )}
                                </div>
                                <div className="flex-grow-1 overflow-hidden">
                                    <h6 className="mb-0 font-size-13 fw-semibold text-white text-truncate">
                                        {c.name}
                                    </h6>
                                    {c.memberNumber && (
                                        <span className="text-muted" style={{ fontSize: '11px' }}>
                                            ID: {c.memberNumber}
                                        </span>
                                    )}
                                </div>
                                <i className="mdi mdi-chevron-right font-size-18 flex-shrink-0" style={{ color: "rgba(255,255,255,0.2)" }} />
                            </div>
                        ))}
                    </SimpleBar>
                )}
            </DropdownMenu>
        </Dropdown>
    );
};

export default GlobalMemberSearch;
